<script setup lang="ts">
/**
 * NotificationToast - Toast Notification Component
 * 
 * Features:
 * - Multiple notification types (success, error, warning, info)
 * - Auto-dismiss with progress indicator
 * - Accessible with ARIA live regions
 * - Keyboard dismissible
 */

import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNotificationStore } from '../../stores/notifications'
import {
  X,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Info,
} from 'lucide-vue-next'
import { TransitionGroup } from 'vue'

const notificationStore = useNotificationStore()
const { t } = useI18n()

function getIcon(type: string) {
  switch (type) {
    case 'success': return CheckCircle
    case 'error': return AlertCircle
    case 'warning': return AlertTriangle
    case 'info': return Info
    default: return Info
  }
}

/**
 * Kaartachtergrond per type: één behandeling voor alle types — een volle,
 * gekleurde ondergrond met witte tekst. Eerder had alleen 'success' die
 * stijl en hielden error/warning/info een witte kaart met gekleurd randje,
 * waardoor bijvoorbeeld de sync-toast (info) er verouderd uitzag naast een
 * succesmelding.
 *
 * De kleuren komen uit de semantische tokens in tailwind.config.js, niet uit
 * losse Tailwind-schaalwaarden — dat is waar die tokens voor bedoeld zijn.
 * Alle vier halen WCAG AA met witte tekst (gemeten: success 5,48:1,
 * error 6,47:1, info 6,70:1, warning 7,09:1 — die laatste zelfs AAA).
 */
function getCardClass(type: string) {
  switch (type) {
    case 'success': return 'bg-success'
    case 'error': return 'bg-error'
    case 'warning': return 'bg-warning'
    case 'info': return 'bg-info'
    default: return 'bg-gray-800'
  }
}
</script>

<template>
  <div 
    class="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full"
    role="region"
    :aria-label="t('toast.regionLabel')"
  >
    <TransitionGroup
      enter-active-class="transition ease-out duration-300"
      enter-from-class="transform translate-x-full opacity-0"
      enter-to-class="transform translate-x-0 opacity-100"
      leave-active-class="transition ease-in duration-200"
      leave-from-class="transform translate-x-0 opacity-100"
      leave-to-class="transform translate-x-full opacity-0"
    >
      <div
        v-for="notification in notificationStore.notifications"
        :key="notification.id"
        class="rounded-lg shadow-lg p-4 text-white"
        :class="getCardClass(notification.type)"
        role="alert"
        :aria-live="notification.type === 'error' ? 'assertive' : 'polite'"
      >
        <div class="flex items-start gap-3">
          <!-- Icon -->
          <component
            :is="getIcon(notification.type)"
            class="w-5 h-5 flex-shrink-0 mt-0.5 text-white"
            aria-hidden="true"
          />

          <!-- Content -->
          <div class="flex-1 min-w-0">
            <h3 class="text-sm font-medium">
              {{ notification.title }}
            </h3>
            <p
              v-if="notification.message"
              class="mt-1 text-sm text-white/90"
            >
              {{ notification.message }}
            </p>
          </div>

          <!-- Dismiss Button -->
          <button
            @click="notificationStore.removeNotification(notification.id)"
            class="p-1 rounded text-white/80 hover:text-white focus:outline-none focus:ring-2 focus:ring-white"
            :aria-label="t('toast.dismiss', { title: notification.title })"
          >
            <X class="w-4 h-4" aria-hidden="true" />
          </button>
        </div>
      </div>
    </TransitionGroup>
  </div>
</template>
